import { lazy, Suspense, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminGuard, ClientGuard, AuthGuard } from "@/components/layout/AuthGuard";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { resolvePanelDestination } from "@/utils/panelRouting";
import { ViewerErrorBoundary } from "@/components/viewer/ViewerErrorBoundary";
import { resolveAppMode } from "@/utils/appMode";
import "@/App.css";

// Each route's page is its own chunk — a visitor to /viewer/:slug never downloads
// the admin panel (and vice versa), and the admin panel never downloads the viewer.
const Login = lazy(() => import("@/pages/Login"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const TenantList = lazy(() => import("@/pages/superadmin/TenantList"));
const DashboardPage = lazy(() => import("@/pages/admin/DashboardPage"));
const ProjectList = lazy(() => import("@/pages/admin/ProjectList"));
const ProjectCreate = lazy(() => import("@/pages/admin/ProjectCreate"));
const ProjectAuthoring = lazy(() => import("@/pages/admin/project/ProjectAuthoring"));
const UsersPage = lazy(() => import("@/pages/admin/UsersPage"));
const LeadsPage = lazy(() => import("@/pages/admin/LeadsPage"));
const SettingsPage = lazy(() => import("@/pages/admin/SettingsPage"));
const Viewer = lazy(() => import("@/pages/viewer/Viewer"));

const ClientDashboard = lazy(() => import("@/pages/client/ClientDashboard"));
const ClientProjectList = lazy(() => import("@/pages/client/ClientProjectList"));
const ClientProjectView = lazy(() => import("@/pages/client/ClientProjectView"));

function RouteFallback() {
  return (
    <div className="h-screen flex items-center justify-center bg-background">
      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// ViewerRouteFallback: shown only while the Viewer chunk itself is downloading
// (typically a few ms). Same backdrop as Visual360Loader (V360_LOADER_BG —
// duplicated here rather than imported so the loader stays out of the main
// chunk) — no spinner — so this never flashes as a separate loading screen
// before Viewer mounts and shows its own branded loader.
function ViewerRouteFallback() {
  return <div style={{
    position: "fixed", inset: 0,
    background: "radial-gradient(120% 90% at 50% 32%, #191338 0%, #0d0a1e 72%) #0d0a1e",
  }} />;
}

function SmartAdminRoot() {
  const { user } = useAuth();
  if (!user) return null;
  return <Navigate to={user.role === "super_admin" ? "dashboard" : "projects"} replace />;
}

function SmartRoot() {
  const { user, isLoading } = useAuth();
  if (isLoading) return (
    <div className="h-screen flex items-center justify-center bg-background">
      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // In subdomain mode this may hop to a sibling subdomain (carrying the
  // session); dest is null while that full-page redirect is in flight.
  const dest = resolvePanelDestination(user.role);
  if (!dest) return (
    <div className="h-screen flex items-center justify-center bg-background">
      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
  return <Navigate to={dest} replace />;
}

function App() {
  // resolveAppMode() reads window.location.hostname — a plain, synchronous
  // read of the current page's host, not something that changes without a
  // full page load. Computed once per App mount (not per route change).
  const hostMode = useMemo(() => resolveAppMode(), []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ImpersonationBanner />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* ── Subdomain-mode routes — mounted on "/" ───────────────────────────
              Second routing layer, driven by resolveAppMode(). Only the branch
              matching the detected hostname mode is registered, so there is
              never more than one "/" route competing at a time. This coexists
              with the path-based routes below (unchanged), which keep serving
              the current Firebase Hosting path-based deploy. */}
          {hostMode.mode === "admin" && (
            <>
              <Route
                path="/"
                element={
                  <AdminGuard>
                    <AdminLayout />
                  </AdminGuard>
                }
              >
                <Route index element={<SmartAdminRoot />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="tenants" element={<TenantList />} />
                <Route path="projects" element={<ProjectList />} />
                <Route path="projects/new" element={<ProjectCreate />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
              <Route
                path="/projects/:id/*"
                element={
                  <AdminGuard>
                    <ProjectAuthoring />
                  </AdminGuard>
                }
              />
            </>
          )}

          {hostMode.mode === "client" && (
            <Route
              path="/"
              element={
                <ClientGuard>
                  <ClientLayout />
                </ClientGuard>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<ClientDashboard />} />
              <Route path="projects" element={<ClientProjectList />} />
              <Route path="projects/:id" element={<ClientProjectView />} />
              <Route path="leads" element={<LeadsPage />} />
            </Route>
          )}

          {hostMode.mode === "viewer" && (
            <>
              {/* React Router ranks an exact "/" above a "/*" splat, so at
                  the bare root URL the legacy "/" → LandingPage route below
                  would otherwise win even in viewer mode. Register both. */}
              <Route path="/" element={<ViewerErrorBoundary><Viewer /></ViewerErrorBoundary>} />
              <Route path="/*" element={<ViewerErrorBoundary><Viewer /></ViewerErrorBoundary>} />
            </>
          )}

          {/* hostMode.mode === "landing" needs no extra route here — the
              existing "/" → LandingPage route below already covers it. */}

          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/set-password" element={<SetPassword />} />

          {/* Admin Panel — Visual team only */}
          <Route
            path="/admin"
            element={
              <AdminGuard>
                <AdminLayout />
              </AdminGuard>
            }
          >
            <Route index element={<SmartAdminRoot />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="tenants" element={<TenantList />} />
            <Route path="projects" element={<ProjectList />} />
            <Route path="projects/new" element={<ProjectCreate />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Project Authoring — own full-screen layout */}
          <Route
            path="/admin/projects/:id/*"
            element={
              <AdminGuard>
                <ProjectAuthoring />
              </AdminGuard>
            }
          />

          {/* Client Panel — commercial roles only */}
          <Route
            path="/app"
            element={
              <ClientGuard>
                <ClientLayout />
              </ClientGuard>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<ClientDashboard />} />
            <Route path="projects" element={<ClientProjectList />} />
            <Route path="projects/:id" element={<ClientProjectView />} />
            <Route path="leads" element={<LeadsPage />} />
          </Route>

          {/* Viewer — public */}
          <Route
            path="/viewer/:slug/*"
            element={
              <ViewerErrorBoundary>
                <Suspense fallback={<ViewerRouteFallback />}>
                  <Viewer />
                </Suspense>
              </ViewerErrorBoundary>
            }
          />

          {/* Landing Page — public root */}
          <Route path="/" element={<LandingPage />} />

          {/* /dashboard — universal platform entry point */}
          <Route path="/dashboard" element={<SmartRoot />} />

          {/* Smart redirect for authenticated users on unknown paths */}
          <Route path="*" element={<SmartRoot />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
